import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { ZoomIn, ZoomOut, Check, X } from 'lucide-react';

function createCroppedImage(imageSrc, pixelCrop) {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.92);
    };
    image.src = imageSrc;
  });
}

export default function ImageCropper({ image, onCropDone, onCancel, cropShape, aspect }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    const blob = await createCroppedImage(image, croppedAreaPixels);
    onCropDone(blob);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" data-testid="image-cropper-modal">
      <div className="bg-white rounded-2xl w-[95vw] max-w-lg overflow-hidden shadow-2xl">
        <div className="relative w-full" style={{ height: '60vh', maxHeight: '420px' }}>
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            cropShape={cropShape === 'round' ? 'round' : 'rect'}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={cropShape !== 'round'}
          />
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex items-center gap-3">
            <ZoomOut className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[#00C48C] h-1.5"
              data-testid="crop-zoom-slider"
            />
            <ZoomIn className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1 rounded-full"
              onClick={onCancel}
              data-testid="crop-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              className="flex-1 rounded-full bg-[#00C48C] hover:bg-[#00a876] text-white"
              onClick={handleConfirm}
              data-testid="crop-confirm-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Confirmer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
